import { useMemo, useRef, useState } from 'react';
import { parseDelimited } from '@/utils/csv-helper';
import { useAuth } from '@/hooks/useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CheckCircle, Clock, X, Search, Download, AlertCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';

export function RemittancePage() {
  // 'ALL' sends no orderType, and both `list` and `summary` already treat it as optional — so a
  // search by AWB, merchant or date range covers B2C and B2B together. Looking up one AWB should
  // not require knowing which of the two tabs it lives on.
  const [tab, setTab] = useState<'ALL' | 'B2C' | 'B2B'>('ALL');
  const [status, setStatus] = useState<'UNPAID' | 'PAID'>('UNPAID');
  const [exporting, setExporting] = useState(false);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [showPay, setShowPay] = useState(false);
  const [awbSearch, setAwbSearch] = useState('');
  const [merchantIds, setMerchantIds] = useState<(string | number)[]>([]);
  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [transactionId, setTransactionId] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const { user: _au } = useAuth();
  const isMerchant = _au?.kind === 'merchant';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [paidFromDate, setPaidFromDate] = useState('');
  const [paidToDate, setPaidToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showBulkPay, setShowBulkPay] = useState(false);
  const [showBulkAdjust, setShowBulkAdjust] = useState(false);

  const params = useMemo(() => {
    const p: any = { status };
    if (tab !== 'ALL') p.orderType = tab;   // omitted ⇒ both B2C and B2B
    if (awbSearch.trim()) p.reference = awbSearch.trim();   // matches AWB **or** LR, so one box serves both types
    if (!isMerchant && merchantIds.length) p.merchantIds = merchantIds.join(',');
    if (courierIds.length) p.courierIds = courierIds.join(',');
    if (transactionId.trim()) p.transactionId = transactionId.trim();
    if (utrNumber.trim()) p.utrNumber = utrNumber.trim();
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    if (paidFromDate) p.paidFromDate = paidFromDate;
    if (paidToDate) p.paidToDate = paidToDate;
    return p;
  }, [tab, status, awbSearch, merchantIds, courierIds, transactionId, utrNumber, fromDate, toDate, paidFromDate, paidToDate, isMerchant]);

  const { data: summary } = useQuery({
    queryKey: ['remittance-summary', tab],
    queryFn: async () => (await api.get('/remittance/summary', { params: tab === 'ALL' ? {} : { orderType: tab } })).data as any,
  });

  /**
   * Export exactly what is on screen. The button used to have no onClick at all — it rendered,
   * did nothing when pressed, and there was no export route behind it either.
   */
  const doExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
      ).toString();
      await downloadBlob(
        `/remittance/export?${qs}`,
        `remittance-${tab.toLowerCase()}-${status.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Export failed');
    } finally { setExporting(false); }
  };
  const { data, isLoading } = useQuery({
    queryKey: ['remittance', params, page, pageSize],
    queryFn: async () => (await api.get('/remittance', { params: { ...params, page, pageSize } })).data as any,
  });
  const { data: merchants } = useQuery({
    enabled: !isMerchant,
    queryKey: ['merchants-light'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchantList: any[] = Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? [];
  const { data: couriers } = useQuery({
    queryKey: ['couriers-light'],
    queryFn: async () => (await api.get('/couriers', { params: { pageSize: 500 } })).data,
  });
  const courierList: any[] = Array.isArray(couriers) ? couriers : (couriers as any)?.items ?? [];

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const selectedIds = Object.keys(selected).filter((k) => selected[Number(k)]).map(Number);
  const selectedTotal = items.filter((r: any) => selected[r.id]).reduce((s: number, r: any) => s + Number(r.codCollected), 0);

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-4">
        <Banknote size={22} className="text-emerald-700" /> Remittance
      </h1>

      {/* Tab */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['ALL', 'B2C', 'B2B'] as const).map((t) => (
          <button key={t} onClick={() => { setTab(t); setSelected({}); }}
            className={`px-5 py-1.5 text-sm rounded-md transition ${tab === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t === 'ALL' ? 'B2C + B2B' : t}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card label="Today" amount={summary?.today.amount} count={summary?.today.count} accent="emerald" />
        <Card label="Yesterday" amount={summary?.yesterday.amount} count={summary?.yesterday.count} />
        <Card label="This Month" amount={summary?.thisMonth.amount} count={summary?.thisMonth.count} accent="blue" />
        <Card label="Total" amount={summary?.total.amount} count={summary?.total.count} accent="violet" />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card label="UNPAID to Merchant" amount={summary?.unpaid.amount} count={summary?.unpaid.count} accent="amber" big />
        <Card label="PAID to Merchant" amount={summary?.paid.amount} count={summary?.paid.count} accent="emerald" big />
      </div>

      {/* Status sub-tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['UNPAID', 'PAID'] as const).map((t) => (
          <button key={t} onClick={() => { setStatus(t); setSelected({}); }}
            className={`px-4 py-1.5 text-sm rounded-md transition ${status === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t === 'UNPAID' ? 'Unpaid' : 'Paid'}
          </button>
        ))}
      </div>

      {/* Filters — the same set the order listing offers, plus the payment-side ones (transaction
          id, UTR, paid-date range) that only make sense here. */}
      <div className="grid grid-cols-12 gap-3 mb-3">
        <div className="col-span-3">
          <label className="text-xs text-gray-500">AWB / LR</label>
          <div className="flex items-center gap-1 border rounded px-2 py-1.5">
            <Search size={14} className="text-slate-400" />
            <input value={awbSearch} onChange={(e) => { setAwbSearch(e.target.value); setPage(1); }} placeholder="Search…" className="flex-1 text-sm outline-none" />
          </div>
        </div>
        {!isMerchant && (
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Merchant</label>
          <MultiSelectFilter label="" placeholder="All merchants" values={merchantIds}
            onChange={(v) => { setMerchantIds(v); setPage(1); }}
            options={merchantList.map((m: any) => ({ value: m.id, label: (m.brandName || m.companyName) + ' (#' + m.id + ')' }))} />
        </div>
        )}
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Courier</label>
          <MultiSelectFilter label="" placeholder="All couriers" values={courierIds}
            onChange={(v) => { setCourierIds(v); setPage(1); }}
            options={courierList.map((c: any) => ({ value: c.id, label: c.name + ' (#' + c.id + ')' }))} />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Delivered Date Range</label>
          <div className="flex gap-1">
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} className="flex-1 border rounded px-2 py-1.5 text-sm" />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} className="flex-1 border rounded px-2 py-1.5 text-sm" />
          </div>
        </div>

        <div className="col-span-3">
          <label className="text-xs text-gray-500">Transaction ID</label>
          <input value={transactionId} onChange={(e) => { setTransactionId(e.target.value); setPage(1); }}
            placeholder="Bank txn ref…" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">UTR Number</label>
          <input value={utrNumber} onChange={(e) => { setUtrNumber(e.target.value); setPage(1); }}
            placeholder="UTR…" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Paid Date Range</label>
          <div className="flex gap-1">
            <input type="date" value={paidFromDate} onChange={(e) => { setPaidFromDate(e.target.value); setPage(1); }} className="flex-1 border rounded px-2 py-1.5 text-sm" />
            <input type="date" value={paidToDate} onChange={(e) => { setPaidToDate(e.target.value); setPage(1); }} className="flex-1 border rounded px-2 py-1.5 text-sm" />
          </div>
        </div>
        <div className="col-span-3 flex items-end">
          <button onClick={() => {
            setAwbSearch(''); setMerchantIds([]); setCourierIds([]); setTransactionId(''); setUtrNumber('');
            setFromDate(''); setToDate(''); setPaidFromDate(''); setPaidToDate(''); setPage(1);
          }} className="border border-slate-300 text-slate-700 px-3 py-2 rounded text-sm hover:bg-slate-50">Reset filters</button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {!isMerchant && status === 'UNPAID' && (
          <button onClick={() => setShowPay(true)} disabled={selectedIds.length === 0}
            className="bg-emerald-600 text-white px-4 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-50">
            <CheckCircle size={14} /> Mark Paid ({selectedIds.length})
          </button>
        )}
        {!isMerchant && (
          <>
            <button onClick={() => setShowBulkPay(true)}
              title="Upload a spreadsheet of payouts — transaction id, transaction date, AWB no, paid amount, payment mode"
              className="border border-emerald-600 text-emerald-700 px-3 py-2 rounded text-sm flex items-center gap-1 hover:bg-emerald-50">
              <Upload size={14} /> Bulk Paid (Excel)
            </button>
            <button onClick={() => setShowBulkAdjust(true)}
              title="Upload a spreadsheet of adjustments — AWB no, adjust amount, adjust method, reference / invoice no"
              className="border border-amber-500 text-amber-700 px-3 py-2 rounded text-sm flex items-center gap-1 hover:bg-amber-50">
              <Upload size={14} /> Bulk Adjustment (Excel)
            </button>
          </>
        )}
        <button onClick={doExport} disabled={exporting}
          title={`Download every ${tab === 'ALL' ? 'B2C + B2B' : tab} ${status.toLowerCase()} row matching the filters above`}
          className="border border-emerald-600 text-emerald-700 px-3 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-50 ml-auto">
          <Download size={14} /> {exporting ? 'Exporting…' : 'Export'}
        </button>
      </div>

      {selectedIds.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-sm flex items-center gap-2">
          <AlertCircle size={14} className="text-amber-600" />
          <b>{selectedIds.length}</b> selected · Total to pay: <b className="text-emerald-700">₹{selectedTotal.toFixed(2)}</b>
        </div>
      )}

      {/* Pagination — the list had none at all, so it silently showed the first 50 rows of however
          many matched and there was no way to reach the rest. */}
      <div className="flex items-center justify-between px-1 py-2 text-xs text-slate-500">
        <span>{selectedIds.length} selected</span>
        <span>Rows per page:&nbsp;
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); setSelected({}); }}
            className="text-xs border border-slate-200 rounded px-1 py-0.5">
            {[25, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          &nbsp;&nbsp;{total === 0 ? 0 : ((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}&nbsp;&nbsp;
          {/* Selection is cleared on a page change: "Mark Paid" acts on ids, and carrying a
              selection across pages means paying rows the operator can no longer see. */}
          <button disabled={page <= 1} onClick={() => { setPage(p => p - 1); setSelected({}); }} className="px-1 disabled:opacity-30">‹</button>
          <button disabled={page * pageSize >= total} onClick={() => { setPage(p => p + 1); setSelected({}); }} className="px-1 disabled:opacity-30">›</button>
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              {!isMerchant && status === 'UNPAID' && <th className="px-2 py-2"><input type="checkbox" onChange={(e) => {
                const c = e.target.checked; const s: any = {};
                if (c) items.forEach((r: any) => { s[r.id] = true; });
                setSelected(s);
              }} /></th>}
              {!isMerchant && <th className="px-2 py-2 text-left">Merchant</th>}
              {/* On the combined view the two order types are interleaved, so the row has to say
                  which it is — otherwise a bare number gives no clue whether it's an AWB or an LR. */}
              {tab === 'ALL' && <th className="text-left">Type</th>}
              <th className="text-left">Courier</th>
              <th className="text-left">{tab === 'B2C' ? 'AWB' : tab === 'B2B' ? 'LR' : 'AWB / LR'}</th>
              <th className="text-left">Delivered</th>
              <th className="text-right">COD Collected</th>
              <th className="text-right">Adjustment</th>
              <th className="text-right">Payable</th>
              <th>Status</th>
              {status === 'PAID' && <th className="text-left">Txn ID</th>}
              {status === 'PAID' && <th className="text-left">UTR</th>}
              {status === 'PAID' && <th className="text-left">Mode</th>}
              {status === 'PAID' && <th className="text-left">Bank</th>}
              {status === 'PAID' && <th className="text-left">Paid At</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">No remittance entries</td></tr>
              : items.map((r: any) => (
                <tr key={r.id} className="border-t">
                  {!isMerchant && status === 'UNPAID' && <td className="px-2 py-2"><input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected({ ...selected, [r.id]: e.target.checked })} /></td>}
                  {!isMerchant && <td className="px-2 py-2">{r.merchant?.companyName ?? '—'}</td>}
                  {tab === 'ALL' && (
                    <td>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.orderType === 'B2B' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                        {r.orderType ?? '—'}
                      </span>
                    </td>
                  )}
                  <td>{r.courier?.name ?? '—'}</td>
                  {/* Pick the reference from the ROW's own type, not the active tab — on the
                      combined view the tab says nothing about any individual row. */}
                  <td className="font-mono text-xs">{r.orderType === 'B2C' ? (r.awbNo ?? r.lrNo) : (r.lrNo ?? r.awbNo)}</td>
                  <td className="text-xs">{r.deliveredAt ? new Date(r.deliveredAt).toLocaleDateString() : '—'}</td>
                  <td className="text-right font-semibold text-emerald-700">₹{r.codCollected.toFixed(2)}</td>
                  <td className="text-right text-xs">
                    {r.adjustmentAmount == null ? <span className="text-slate-300">—</span> : (
                      <span className={r.adjustmentAmount < 0 ? 'text-rose-600' : 'text-emerald-600'}
                        title={[r.adjustmentMethod, r.adjustmentRef].filter(Boolean).join(' · ')}>
                        {r.adjustmentAmount < 0 ? '−' : '+'}₹{Math.abs(r.adjustmentAmount).toFixed(2)}
                        {r.adjustmentRef && <div className="text-[10px] text-slate-400 font-mono">{r.adjustmentRef}</div>}
                      </span>
                    )}
                  </td>
                  <td className="text-right font-medium">₹{Number(r.expectedAmount ?? r.codCollected).toFixed(2)}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] ${r.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : r.status === 'HOLD' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{r.status}</span></td>
                  {status === 'PAID' && <td className="font-mono text-xs">{r.transactionId ?? '—'}</td>}
                  {status === 'PAID' && <td className="font-mono text-xs">{r.utrNumber ?? '—'}</td>}
                  {status === 'PAID' && <td className="text-xs">{r.paymentMode ?? '—'}</td>}
                  {status === 'PAID' && <td className="text-xs">{r.bankName ?? '—'}</td>}
                  {status === 'PAID' && <td className="text-xs">{r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}</td>}
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 mt-2">{items.length} of {total.toLocaleString()} entries</div>

      {showPay && (
        <PayModal
          ids={selectedIds}
          total={selectedTotal}
          onClose={() => setShowPay(false)}
          onDone={() => { setShowPay(false); setSelected({}); qc.invalidateQueries({ queryKey: ['remittance'] }); qc.invalidateQueries({ queryKey: ['remittance-summary'] }); }}
        />
      )}

      {showBulkPay && (
        <BulkImportModal
          kind="pay"
          onClose={() => setShowBulkPay(false)}
          onDone={() => { setSelected({}); qc.invalidateQueries({ queryKey: ['remittance'] }); qc.invalidateQueries({ queryKey: ['remittance-summary'] }); }}
        />
      )}
      {showBulkAdjust && (
        <BulkImportModal
          kind="adjust"
          onClose={() => setShowBulkAdjust(false)}
          onDone={() => { setSelected({}); qc.invalidateQueries({ queryKey: ['remittance'] }); qc.invalidateQueries({ queryKey: ['remittance-summary'] }); }}
        />
      )}
    </div>
  );
}

// ── Bulk import (Mark Paid / Adjustment) ─────────────────────────────────────────────────────────
// One component for both files: they differ only in their columns and their endpoint, and keeping
// the parse / validate / preview / commit flow in one place means the two can never drift apart.

type BulkKind = 'pay' | 'adjust';

const BULK_SPEC: Record<BulkKind, {
  title: string;
  endpoint: string;
  /** Sheet column header → the key the backend reads. */
  columns: Array<{ header: string; key: string; required: boolean; hint: string }>;
  sample: string[][];
  note: string;
}> = {
  pay: {
    title: 'Bulk Mark as Paid',
    endpoint: '/remittance/bulk-pay',
    columns: [
      { header: 'Transaction ID', key: 'transactionId', required: true, hint: 'bank reference for the payout' },
      { header: 'Transaction Date', key: 'transactionDate', required: true, hint: 'DD-MM-YYYY' },
      { header: 'AWB No', key: 'awbNo', required: true, hint: 'AWB (B2C) or LR (B2B)' },
      { header: 'Paid Amt', key: 'paidAmount', required: true, hint: 'amount actually paid, in ₹' },
      { header: 'Payment Mode', key: 'paymentMode', required: false, hint: 'NEFT / RTGS / IMPS / UPI / CHEQUE / CASH' },
      { header: 'UTR Number', key: 'utrNumber', required: false, hint: 'optional' },
      { header: 'Bank Name', key: 'bankName', required: false, hint: 'optional' },
    ],
    sample: [
      ['HDFC0099231', '05-08-2026', '21194911524795', '1250.00', 'NEFT', 'AXISN0912311', 'HDFC Bank'],
      ['HDFC0099231', '05-08-2026', '21194911728742', '899.50', 'NEFT', 'AXISN0912311', 'HDFC Bank'],
    ],
    note: 'Rows already marked PAID are skipped, never paid twice. A paid amount that differs from the COD collected is recorded as a discrepancy rather than overwriting the collected figure.',
  },
  adjust: {
    title: 'Bulk Remittance Adjustment',
    endpoint: '/remittance/bulk-adjust',
    columns: [
      { header: 'AWB No', key: 'awbNo', required: true, hint: 'AWB (B2C) or LR (B2B)' },
      { header: 'Adjust Amt', key: 'adjustAmount', required: true, hint: 'amount in ₹ (sign comes from the method)' },
      { header: 'Adjust Method', key: 'adjustMethod', required: true, hint: 'DEDUCTION / ADDITION / WRITE_OFF / CREDIT_NOTE / DEBIT_NOTE' },
      { header: 'Reference No', key: 'referenceNo', required: true, hint: 'reference or invoice no' },
    ],
    sample: [
      ['21194911524795', '150.00', 'DEDUCTION', 'INV-2026-0912'],
      ['298043335', '75.50', 'CREDIT_NOTE', 'CN-2026-0033'],
    ],
    note: 'The adjustment is stored in its own column and the Payable figure is recomputed — COD Collected is never altered, so it keeps matching the courier statement. Shipments already PAID are refused: adjust the next payout instead.',
  },
};

function bulkSampleCsv(kind: BulkKind): string {
  const spec = BULK_SPEC[kind];
  return [spec.columns.map(c => c.header).join(','), ...spec.sample.map(r => r.join(','))].join('\r\n');
}

function BulkImportModal({ kind, onClose, onDone }: { kind: BulkKind; onClose: () => void; onDone: () => void }) {
  const spec = BULK_SPEC[kind];
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<any>(null);   // dry-run result
  const [result, setResult] = useState<any>(null);     // committed result
  const fileRef = useRef<HTMLInputElement>(null);

  const rows = useMemo(() => parseBulkCsv(text, spec.columns), [text, spec.columns]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    const name = f.name.toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      toast.error('Please import a CSV file. In Excel use File → Save As → "CSV (Comma delimited)", then import that file.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    const r = new FileReader();
    r.onload = (ev) => { setText(String(ev.target?.result ?? '')); setFileName(f.name); setPreview(null); setResult(null); };
    r.onerror = () => toast.error('Could not read that file.');
    r.readAsText(f);
  };

  const downloadTemplate = () => {
    const blob = new Blob([bulkSampleCsv(kind)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `remittance-${kind === 'pay' ? 'bulk-paid' : 'bulk-adjustment'}-template.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
  };

  // Validate first, ALWAYS. This file moves money against real shipments, and a half-applied import
  // leaves the panel and the bank statement permanently out of step — so nothing is written until
  // the operator has seen every rejected line and pressed Apply on what remains.
  const validate = useMutation({
    mutationFn: async () => (await api.post(spec.endpoint, { rows, dryRun: true })).data,
    onSuccess: (d: any) => setPreview(d),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Validation failed'),
  });
  const commit = useMutation({
    mutationFn: async () => (await api.post(spec.endpoint, { rows, dryRun: false })).data,
    onSuccess: (d: any) => {
      setResult(d);
      toast.success(`${d.ok} row(s) applied${d.failed ? `, ${d.failed} failed` : ''}`);
      onDone();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Import failed'),
  });

  const errors = (result ?? preview)?.errors ?? [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-10 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-[860px] mb-10">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><Upload size={16} /> {spec.title}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
            <div className="font-medium mb-1">Columns</div>
            <ul className="text-xs space-y-0.5">
              {spec.columns.map(c => (
                <li key={c.key}>
                  <code className="font-mono">{c.header}</code>
                  {c.required ? <span className="text-rose-600"> *</span> : <span className="text-slate-400"> (optional)</span>}
                  <span className="text-slate-500"> — {c.hint}</span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-emerald-800 mt-2">{spec.note}</div>
            <button onClick={downloadTemplate}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">
              <Download size={12} /> Download template
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Import CSV file</label>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile}
              className="text-sm w-full file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:text-sm file:bg-emerald-600 file:text-white file:cursor-pointer cursor-pointer" />
            {fileName && <div className="text-xs text-emerald-700 mt-1">{fileName} — {rows.length} row(s) loaded</div>}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Or paste CSV content</label>
            <textarea value={text} onChange={(e) => { setText(e.target.value); setFileName(''); setPreview(null); setResult(null); }}
              rows={6} placeholder={bulkSampleCsv(kind)} className="w-full border rounded px-2 py-1.5 font-mono text-xs" />
          </div>

          {(preview || result) && (
            <div className="rounded border">
              <div className="grid grid-cols-4 divide-x text-center">
                <div className="p-3"><div className="text-xs text-slate-500">Rows</div><div className="text-xl font-bold">{rows.length}</div></div>
                <div className="p-3 bg-emerald-50"><div className="text-xs text-emerald-700">{result ? 'Applied' : 'Will apply'}</div><div className="text-xl font-bold text-emerald-700">{(result ?? preview).ok}</div></div>
                <div className="p-3 bg-rose-50"><div className="text-xs text-rose-700">Rejected</div><div className="text-xl font-bold text-rose-700">{(result ?? preview).failed}</div></div>
                <div className="p-3">
                  <div className="text-xs text-slate-500">{kind === 'pay' ? 'Total' : 'Net adjustment'}</div>
                  <div className="text-xl font-bold">₹{Number(kind === 'pay' ? (result ?? preview).totalAmount ?? 0 : (result ?? preview).totalAdjusted ?? 0).toFixed(2)}</div>
                </div>
              </div>
              {kind === 'pay' && (result ?? preview).alreadyPaid > 0 && (
                <div className="px-3 py-2 bg-amber-50 text-xs text-amber-800 border-t">
                  {(result ?? preview).alreadyPaid} row(s) were already marked PAID and were skipped — they have not been paid twice.
                </div>
              )}
              {errors.length > 0 && (
                <div className="p-3 bg-rose-50 text-xs border-t">
                  <div className="font-medium mb-1 flex items-center gap-1"><AlertCircle size={12} /> Rejected rows — fix these in the file and re-import:</div>
                  <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                    {errors.map((e: any, i: number) => (
                      <li key={i}>Row {e.row}{e.reference && <b> ({e.reference})</b>}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-slate-300 text-slate-700 px-4 py-1.5 rounded text-sm">Close</button>
          <button onClick={() => validate.mutate()} disabled={rows.length === 0 || validate.isPending || !!result}
            className="border border-emerald-600 text-emerald-700 px-4 py-1.5 rounded text-sm disabled:opacity-50">
            {validate.isPending ? 'Checking…' : 'Validate'}
          </button>
          <button onClick={() => commit.mutate()} disabled={!preview || preview.ok === 0 || commit.isPending || !!result}
            title={!preview ? 'Validate the file first' : undefined}
            className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
            {commit.isPending ? 'Applying…' : `Apply ${preview ? preview.ok : ''} row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Split one CSV line, honouring quoted fields (a bank narration can contain a comma). */

/**
 * Map spreadsheet headers onto the keys the backend reads.
 *
 * Matching is on the squashed header ("Paid Amt (₹)" → "paidamt"), because these files are typed by
 * hand and by three different people: the same column comes through as "AWB No", "awb_no" and
 * "Waybill". A header that isn't recognised is passed through untouched rather than dropped, so a
 * column the backend knows about under its exact key still works.
 */
const BULK_ALIASES: Record<string, string> = {
  transactionid: 'transactionId', txnid: 'transactionId', transactionno: 'transactionId', utrid: 'transactionId',
  transactiondate: 'transactionDate', txndate: 'transactionDate', paiddate: 'transactionDate', paymentdate: 'transactionDate', date: 'transactionDate',
  awb: 'awbNo', awbno: 'awbNo', awbnumber: 'awbNo', waybill: 'awbNo', waybillno: 'awbNo',
  lr: 'awbNo', lrno: 'awbNo', lrnumber: 'awbNo', awbnolrno: 'awbNo',
  paidamt: 'paidAmount', paidamount: 'paidAmount', amountpaid: 'paidAmount', amount: 'paidAmount',
  paymentmode: 'paymentMode', mode: 'paymentMode', paymentmethod: 'paymentMode',
  utr: 'utrNumber', utrnumber: 'utrNumber', utrno: 'utrNumber',
  bank: 'bankName', bankname: 'bankName',
  adjustamt: 'adjustAmount', adjustamount: 'adjustAmount', adjustmentamount: 'adjustAmount', adjustmentamt: 'adjustAmount',
  adjustmethod: 'adjustMethod', adjustmentmethod: 'adjustMethod', method: 'adjustMethod',
  referenceno: 'referenceNo', reference: 'referenceNo', refno: 'referenceNo',
  invoiceno: 'referenceNo', invoice: 'referenceNo', referencenoinvoiceno: 'referenceNo',
  notes: 'notes', remarks: 'notes', note: 'notes',
};

function canonicalBulkHeader(h: string): string {
  const squashed = h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '');
  return BULK_ALIASES[squashed] ?? h.trim();
}

/** Remittance bulk rows, via the shared quote-aware tokeniser (see utils/csv-helper). */
function parseBulkCsv(text: string, _columns: Array<{ key: string }>): Array<Record<string, string>> {
  return parseDelimited(text, { mapHeader: canonicalBulkHeader }).rows;
}

function Card({ label, amount, count, accent, big }: any) {
  const c = accent === 'emerald' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : accent === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-700' : accent === 'blue' ? 'border-blue-200 bg-blue-50 text-blue-700' : accent === 'violet' ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-slate-200 bg-slate-50 text-slate-700';
  return (
    <div className={`p-3 rounded border ${c}`}>
      <div className="text-xs uppercase">{label}</div>
      <div className={`font-bold ${big ? 'text-2xl' : 'text-xl'}`}>₹{Number(amount ?? 0).toLocaleString()}</div>
      <div className="text-[10px]">{count ?? 0} shipments</div>
    </div>
  );
}

function PayModal({ ids, total, onClose, onDone }: any) {
  const [form, setForm] = useState<any>({ paidAt: new Date().toISOString().slice(0, 10) });
  const pay = useMutation({
    mutationFn: async () => (await api.post('/remittance/pay', { ids, ...form })).data,
    onSuccess: (d: any) => { alert(`Paid ${d.paidIds.length} shipments · ₹${d.totalAmount.toFixed(2)} across ${d.batches.length} batch(es)`); onDone(); },
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[640px]">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><CheckCircle size={16} /> Mark as Paid</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
            <div className="text-xs text-emerald-700">Total to pay across {ids.length} shipment(s):</div>
            <div className="text-2xl font-bold text-emerald-800">₹{Number(total ?? 0).toFixed(2)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-gray-500 mb-1">Bank Transaction ID *</label><input value={form.transactionId ?? ''} onChange={(e) => setForm({ ...form, transactionId: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">UTR Number</label><input value={form.utrNumber ?? ''} onChange={(e) => setForm({ ...form, utrNumber: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Bank Name</label><input value={form.bankName ?? ''} onChange={(e) => setForm({ ...form, bankName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Bank A/C No</label><input value={form.bankAccountNo ?? ''} onChange={(e) => setForm({ ...form, bankAccountNo: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></div>
            <div className="col-span-2"><label className="block text-xs text-gray-500 mb-1">Paid Date</label><input type="date" value={form.paidAt} onChange={(e) => setForm({ ...form, paidAt: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
            <div className="col-span-2"><label className="block text-xs text-gray-500 mb-1">Notes</label><textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
          </div>
          <div className="text-xs text-slate-500"><Clock size={11} className="inline" /> Selected AWBs will be grouped per merchant into payment batches and closed.</div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => pay.mutate()} disabled={pay.isPending || !form.transactionId} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{pay.isPending ? 'Processing…' : 'Confirm Payment'}</button>
        </div>
      </div>
    </div>
  );
}
